import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from './client'

export interface User {
  id: string
  email: string
  display_name: string
  avatar_url: string | null
  // Per-user Co-pilot rep phone (the number the PSTN bridge rings first).
  // Self-serve on the Profile page; null until the user sets it.
  rep_phone_number: string | null
  // Per-user default Co-pilot call method: 'pstn' (dial my phone) or 'webrtc'
  // (take it in the browser). Self-serve on the Profile page; null = no saved
  // preference (the staging page then defaults to phone).
  copilot_call_transport: 'pstn' | 'webrtc' | null
}

export interface Org {
  id: string
  name: string
  slug: string
  status: string
  spend_cap_daily_usd: number
  spend_cap_monthly_usd: number
  default_caller_id: string | null
  rep_phone_number: string | null
}

export type Role = 'owner' | 'admin' | 'member' | 'viewer'

export interface Membership {
  org_id: string
  user_id: string
  role: Role
  org?: Org
}

export interface Me {
  user: User
  active_org_id: string | null
  memberships: Membership[]
  is_superadmin: boolean
}

export interface Invite {
  id: string
  org_id: string
  email: string
  role: string
  expires_at: number
  created_at: number
  token?: string
  // True when WorkOS sent a branded invitation email; False when the admin
  // must manually share the token URL (the org has no
  // workos_organization_id stamped).
  email_sent: boolean
}

export interface InviteListItem {
  id: string
  org_id: string
  email: string
  role: string
  expires_at: number
  created_at: number
  token: string
}

export function useMe() {
  return useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => apiFetch<Me>('/api/auth/me'),
  })
}

export function useUpdateMe() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: {
      display_name?: string
      rep_phone_number?: string | null
      copilot_call_transport?: 'pstn' | 'webrtc' | null
    }) =>
      apiFetch<Me>('/api/auth/me', {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    onSuccess: (data) => {
      // Seed the cache directly so the UI reflects the rename without a
      // round-trip; invalidation is the safety net if other consumers
      // (sidebar, header) depend on a fresh fetch.
      qc.setQueryData(['auth', 'me'], data)
      qc.invalidateQueries({ queryKey: ['auth', 'me'] })
    },
  })
}

export function useCreateOrg() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { name: string; slug: string }) =>
      apiFetch<Org>('/api/orgs', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['auth', 'me'] }),
  })
}

export function useSwitchOrg() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (org_id: string) =>
      apiFetch<void>('/api/session/switch-org', {
        method: 'POST',
        body: JSON.stringify({ org_id }),
      }),
    onSuccess: () => {
      qc.invalidateQueries()
    },
  })
}

export function useCreateInvite(orgId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { email: string; role?: Role }) =>
      apiFetch<Invite>(`/api/orgs/${orgId}/invites`, {
        method: 'POST',
        body: JSON.stringify({ role: 'member', ...body }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invites', orgId] }),
  })
}

export function useAcceptInvite() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (token: string) =>
      apiFetch<Membership>(`/api/invites/${token}/accept`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['auth', 'me'] }),
  })
}

export function useListInvites(orgId: string, enabled: boolean = true) {
  return useQuery({
    queryKey: ['invites', orgId],
    queryFn: () => apiFetch<InviteListItem[]>(`/api/orgs/${orgId}/invites`),
    enabled: enabled && Boolean(orgId),
  })
}

export function useRevokeInvite(orgId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (token: string) =>
      apiFetch<void>(`/api/orgs/${orgId}/invites/${token}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invites', orgId] }),
  })
}

export function useUpdateMembership(orgId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: Role }) =>
      apiFetch<Membership>(`/api/orgs/${orgId}/members/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify({ role }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['auth', 'me'] })
      qc.invalidateQueries({ queryKey: ['invites', orgId] })
    },
  })
}

export function useDeleteMembership(orgId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) =>
      apiFetch<void>(`/api/orgs/${orgId}/members/${userId}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['auth', 'me'] })
      qc.invalidateQueries({ queryKey: ['invites', orgId] })
    },
  })
}
